import React from 'react';
import type { RequirementListItem } from '@shared/types';
import { useTranslation } from 'react-i18next';

/**
 * RequirementCard Component
 * 
 * Displays a single requirement in list view with summary and metadata.
 * 
 * Requirements:
 * - US4: Requirement list display
 * - Shows summary, complexity, status, visitor info
 */

interface RequirementCardProps {
  requirement: RequirementListItem;
  onClick: () => void;
}

export const RequirementCard: React.FC<RequirementCardProps> = ({
  requirement,
  onClick,
}) => {
  const { t } = useTranslation();

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      PENDING: {
        bg: 'bg-yellow-100 dark:bg-yellow-900',
        text: 'text-yellow-800 dark:text-yellow-200',
        label: t('requirement.status.pending'),
      },
      PROCESSING: {
        bg: 'bg-blue-100 dark:bg-blue-900',
        text: 'text-blue-800 dark:text-blue-200',
        label: t('requirement.status.processing'),
      },
      COMPLETED: {
        bg: 'bg-green-100 dark:bg-green-900',
        text: 'text-green-800 dark:text-green-200',
        label: t('requirement.status.completed'),
      },
      FAILED: {
        bg: 'bg-red-100 dark:bg-red-900',
        text: 'text-red-800 dark:text-red-200',
        label: t('requirement.status.failed'),
      },
    };

    const badge = badges[status] || badges.PENDING;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  const getComplexityBadge = (complexity: string | null) => {
    if (!complexity) return null;

    const badges: Record<string, { bg: string; text: string; label: string }> = {
      LOW: {
        bg: 'bg-gray-100 dark:bg-gray-700',
        text: 'text-gray-800 dark:text-gray-200',
        label: t('requirement.complexity.low'),
      },
      MEDIUM: {
        bg: 'bg-blue-100 dark:bg-blue-900',
        text: 'text-blue-800 dark:text-blue-200',
        label: t('requirement.complexity.medium'),
      },
      HIGH: {
        bg: 'bg-orange-100 dark:bg-orange-900',
        text: 'text-orange-800 dark:text-orange-200',
        label: t('requirement.complexity.high'),
      },
      VERY_HIGH: {
        bg: 'bg-red-100 dark:bg-red-900',
        text: 'text-red-800 dark:text-red-200',
        label: t('requirement.complexity.veryHigh'),
      },
    };

    const badge = badges[complexity] || badges.MEDIUM;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer p-6 border border-gray-200 dark:border-gray-700"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {requirement.summary || t('requirement.noSummary')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {getStatusBadge(requirement.status)}
            {requirement.complexity && getComplexityBadge(requirement.complexity)}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              {requirement.language === 'HU' ? '🇭🇺 Magyar' : '🇬🇧 English'}
            </span>
          </div>
        </div>
      </div>

      {/* Visitor Info */}
      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
        <div>
          <span className="font-medium">{t('requirement.visitor')}:</span>{' '}
          {requirement.visitor.name}
        </div>
        <div>
          <span className="font-medium">{t('requirement.email')}:</span>{' '}
          {requirement.visitor.email}
        </div>
        <div>
          <span className="font-medium">{t('requirement.created')}:</span>{' '}
          {new Date(requirement.createdAt).toLocaleString()}
        </div>
      </div>

      {/* View Button */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
          {t('requirement.viewDetails')} →
        </button>
      </div>
    </div>
  );
};
